import React, { useEffect, useState } from 'react';
import { FormControl, Table } from 'react-bootstrap';
import {
  User,
  ChargebeeSubscription,
  EnrichedSubscription,
  PlanPermission,
} from 'v2/models';
import classnames from 'classnames';
import styles from './reviewLicenses.module.scss';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import { validateRestrictions } from 'v2/utils/licensesUtils';
import CheckboxTooltip from 'v2/common/CheckBox/CheckboxTooltip';

interface LicensesFormProps {
  planPermissions: PlanPermission;
  subscriptions: ChargebeeSubscription[];
  member: User;
  readOnly?: boolean;
}

export interface SubscriptionsChangesField {
  licensesChanges: EnrichedSubscription[];
}

const LicensesForm = ({
  planPermissions,
  subscriptions,
  member,
  readOnly = false,
}: LicensesFormProps) => {
  const { setValue, register } = useFormContext<SubscriptionsChangesField>();
  const initialState: EnrichedSubscription[] = subscriptions.map(
    (cSubscription) => ({
      ...cSubscription,
      permissions: planPermissions[cSubscription.plan_fk.name],
      user_id: member.id,
      selected: cSubscription.users.includes(member.id),
    })
  );
  const [localSubscriptions, setlocalSubscriptions] =
    useState<EnrichedSubscription[]>(initialState);

  useEffect(() => {
    setValue('licensesChanges', _.cloneDeep(localSubscriptions), {
      shouldValidate: true,
    });
  }, [localSubscriptions]);

  const handleSubscriptionChange = (
    isChecked: boolean,
    subscription: ChargebeeSubscription,
    subscriptionIndex: number
  ) => {
    setlocalSubscriptions((local) => {
      const newLocal = _.cloneDeep(local);
      newLocal[subscriptionIndex] = {
        ...subscription,
        permissions: planPermissions[subscription.plan_fk.name],
        user_id: member.id,
        selected: isChecked,
        remaining: isChecked
          ? subscription.remaining - 1
          : subscription.remaining + 1,
      };
      return newLocal;
    });
  };

  return (
    <div
      className={classnames(styles.container, {
        [styles.readOnly]: readOnly,
      })}
    >
      {!readOnly && (
        <span className="mb-5">
          Please select the Licenses you would like to assign to this user.
        </span>
      )}

      <Table className={classnames(styles.table)} bordered={false}>
        <thead>
          <tr>
            <th>License</th>
            <th>Purchased</th>
            <th>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {localSubscriptions.map((subscription, index) => {
            const isDisabled = validateRestrictions(
              subscription,
              localSubscriptions
            );

            return (
              <tr key={subscription.id} data-cy="license-details-row">
                <td>
                  <div
                    aria-disabled={(isDisabled[0] as boolean) && !readOnly}
                    className={classnames('text-capitalize')}
                    data-cy="license-name-text"
                  >
                    {readOnly ? (
                      subscription.plan_fk.name.replaceAll('-', ' ')
                    ) : (
                      <CheckboxTooltip
                        keyText={`subscription_${subscription.id}`}
                        label={subscription.plan_fk.name.replaceAll('-', ' ')}
                        checked={subscription.selected}
                        disabled={isDisabled[0] as boolean}
                        tooltip={isDisabled[1] as string}
                        position="top-start"
                        isDarkTooltip
                        onChange={(elem) =>
                          handleSubscriptionChange(
                            elem.target.checked,
                            subscription,
                            index
                          )
                        }
                      />
                    )}
                  </div>
                </td>
                <td>
                  <p
                    aria-disabled={(isDisabled[0] as boolean) && !readOnly}
                    data-testid={`subscription_${subscription.id}_purchased`}
                    data-cy="license-purchased-amount-text"
                  >
                    {subscription.plan_quantity}
                  </p>
                </td>
                <td>
                  <p
                    aria-disabled={(isDisabled[0] as boolean) && !readOnly}
                    data-testid={`subscription_${subscription.id}_remaining`}
                    data-cy="license-remaining-amount-text"
                  >
                    {subscription.remaining}
                  </p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <FormControl
        type="hidden"
        readOnly={true}
        aria-label="licenses-changes-hidden-input"
        {...register?.('licensesChanges', {
          validate: (value) => !_.isEqual(value, initialState),
        })}
      />
    </div>
  );
};

export default LicensesForm;
